import { useMemo, Fragment } from 'react';
import { Box, Divider, InputLabel, Typography } from '@mui/material';

import { ScaleRatingFormField } from 'types';
import { isSingleScaleRating } from 'utils/scale-rating';
import { QuestionLabel } from 'components/question-label';
import { defaultLanguage, useCourseContext } from 'context';
import {
  questionLabelText,
  hasVisibleQuestionLabel,
  getTranslatedQuestionLabel,
} from 'utils/question-label';

import { ScaleRatingField } from '../fields/scale-rating';

const sharedHeaderLabelSx = {
  whiteSpace: 'normal',
  color: 'gray.neutral',
  paddingBottom: '16px',

  overflowWrap: 'anywhere',
} as const;

interface ScaleHeaderProps {
  min: number;
  count: number;
  labels: string[];
  hasAllLabels: boolean;
  showSimpleHeader: boolean;
}

const ScaleHeader = ({
  min,
  count,
  labels,
  hasAllLabels,
  showSimpleHeader,
}: ScaleHeaderProps) => {
  if (showSimpleHeader) {
    return (
      <div className="mb-1 flex w-full items-center justify-between">
        <Typography component="div" variant="body1" sx={sharedHeaderLabelSx}>
          <QuestionLabel value={labels[0]} />
        </Typography>
        <Typography component="div" variant="body1" sx={sharedHeaderLabelSx}>
          <QuestionLabel value={labels[count - 1]} />
        </Typography>
      </div>
    );
  }

  if (hasAllLabels) {
    return (
      <div className="mb-1 flex w-full items-end">
        {labels.map((labelText, labelIndex) => (
          <Typography
            component="div"
            variant="body1"
            key={min + labelIndex}
            sx={sharedHeaderLabelSx}
            className="flex flex-1 justify-center text-center leading-tight break-words"
          >
            <QuestionLabel value={labelText} />
          </Typography>
        ))}
      </div>
    );
  }

  return (
    <div className="mb-1 flex items-end gap-2 md:gap-2.5">
      {labels.map((labelText, labelIndex) => (
        <Typography
          component="div"
          variant="body1"
          key={min + labelIndex}
          className="w-7 text-center leading-tight break-words md:w-8"
          sx={{
            ...sharedHeaderLabelSx,
            display: 'flex',
            alignItems: 'flex-end',
            justifyContent: 'center',
          }}
        >
          <QuestionLabel value={labelText} />
        </Typography>
      ))}
    </div>
  );
};

export const FormScaleRatingField = ({
  id,
  label,
  options,
  translations,
}: ScaleRatingFormField) => {
  const { language } = useCourseContext();
  const isSingle = isSingleScaleRating(options?.options);
  const min = options?.minValue ?? 1;
  const max = options?.maxValue ?? 7;
  const count = max - min + 1;

  const translation = useMemo(() => {
    const fieldTranslation =
      language && language !== defaultLanguage
        ? translations?.[language]
        : undefined;
    return {
      options: fieldTranslation?.options || options?.options,
      // Explicit translated arrays keep blank entries by index; omitted arrays use the source.
      valueLabels: fieldTranslation?.valueLabels || options?.valueLabels,
      label: getTranslatedQuestionLabel(
        { label, translations },
        language,
        defaultLanguage
      ),
    };
  }, [label, options?.options, options?.valueLabels, language, translations]);

  const { labels, hasAllLabels, showFullHeader, showSimpleHeader } =
    useMemo(() => {
      const computedLabels = Array.from({ length: count }, (_, i) => {
        const rawLabel = translation.valueLabels?.[i]?.trim() ?? '';
        return hasVisibleQuestionLabel(rawLabel) ? rawLabel : '';
      });
      const hasAnyLabel = computedLabels.some(Boolean);
      const hasAllLabels = computedLabels.every(Boolean);
      const hasFirst = !!computedLabels[0];
      const hasLast = !!computedLabels[count - 1];
      const intermediateCount = computedLabels
        .slice(1, Math.max(1, count - 1))
        .filter(Boolean).length;
      const showSimpleHeader = (hasFirst || hasLast) && intermediateCount === 0;

      return {
        hasAllLabels,
        showSimpleHeader,
        labels: computedLabels,
        showFullHeader: hasAnyLabel && !showSimpleHeader,
      };
    }, [count, translation.valueLabels]);

  const visibleQuestionLabel = hasVisibleQuestionLabel(translation.label)
    ? translation.label
    : '';

  // The blank source row identifies a single scale; only its question label is translated.
  const rows = isSingle ? options?.options : translation.options;

  return (
    <Box className="flex flex-col gap-2">
      {!isSingle && visibleQuestionLabel && (
        <InputLabel
          component="div"
          className="mb-2"
          sx={{ whiteSpace: 'pre-line' }}
        >
          <QuestionLabel value={visibleQuestionLabel} />
        </InputLabel>
      )}
      {rows?.map((option, index) => (
        <Fragment key={`${id}-${option}-${index}`}>
          <ScaleRatingField
            min={min}
            max={max}
            name={`${id}.${index}`}
            stretchLayout={hasAllLabels}
            labelClassName="md:self-center"
            fullWidth={isSingle && !visibleQuestionLabel}
            label={
              isSingle
                ? visibleQuestionLabel
                : hasVisibleQuestionLabel(option)
                  ? option
                  : ''
            }
            ariaLabel={
              [
                questionLabelText(visibleQuestionLabel),
                !isSingle && hasVisibleQuestionLabel(option)
                  ? questionLabelText(option)
                  : '',
              ]
                .filter(Boolean)
                .join(': ') || id
            }
            header={
              index === 0 && (showFullHeader || showSimpleHeader) ? (
                <ScaleHeader
                  min={min}
                  count={count}
                  labels={labels}
                  hasAllLabels={hasAllLabels}
                  showSimpleHeader={showSimpleHeader}
                />
              ) : undefined
            }
          />
          {index < rows!.length - 1 && <Divider sx={{ my: 1.5 }} />}
        </Fragment>
      ))}
    </Box>
  );
};
